import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, ChevronUp } from "lucide-react";
import logo from "@/assets/logo-coressen-white.svg";

const navItems = [
  { label: "Método", href: "#metodo", id: "metodo" },
  { label: "Proceso", href: "#proceso", id: "proceso" },
  { label: "Entregables", href: "#entregables", id: "entregables" },
  { label: "Transformación", href: "#transformacion", id: "transformacion" },
  { label: "Inversión", href: "#inversion", id: "inversion" },
];



const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  const { scrollYProgress } = useScroll();

  // Track scroll position for navbar styling and back-to-top button
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setIsScrolled(latest > 0.02);
    setShowBackToTop(latest > 0.15);
  });

  // Intersection Observer for active section detection
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    
    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setActiveSection(item.id);
              }
            });
          },
          { threshold: 0.3, rootMargin: "-80px 0px -50% 0px" }
        );
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const scrollToSection = useCallback((href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);


  return (
    <>
      {/* Scroll Progress Indicator */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary z-[60] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-1 left-0 right-0 z-50"
      >
        {/* Dynamic Background */}
        <motion.div
          className="absolute inset-0 border-b transition-all duration-300"
          animate={{
            backgroundColor: isScrolled ? "hsl(var(--foreground) / 0.97)" : "hsl(var(--foreground) / 0.9)",
            backdropFilter: isScrolled ? "blur(20px)" : "blur(12px)",
            borderColor: "hsl(var(--foreground))",
          }}
        />

        <div className="container relative z-10 px-4">
          <motion.div
            className="flex items-center justify-between"
            animate={{ height: isScrolled ? "56px" : "72px" }}
            transition={{ duration: 0.3 }}
          >
            {/* Logo */}
            <motion.a
              href="https://weare.coressenglobal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="flex items-center justify-center px-2.5 py-2 rounded-xl"
                animate={{ height: isScrolled ? "44px" : "56px" }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={logo}
                  alt="CORESSEN"
                  className="w-auto h-[calc(100%-8px)]"
                />
              </motion.div>
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-0.5">
              {navItems.map((item) => (
                <motion.button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="relative px-4 py-2 text-sm font-medium transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span
                    className={`relative z-10 transition-colors duration-200 ${
                      activeSection === item.id
                        ? "text-background"
                        : "text-background/60 hover:text-background/90"
                    }`}
                  >
                    {item.label}
                  </span>
                  
                  {/* Active indicator */}
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="activeSection"
                      className="absolute inset-0 bg-background/15 rounded-full"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  
                  {/* Hover underline */}
                  <motion.span
                    className="absolute bottom-1 left-1/2 h-0.5 bg-gradient-to-r from-primary to-primary/60 rounded-full"
                    initial={{ width: 0, x: "-50%" }}
                    whileHover={{ width: "60%" }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.button>
              ))}
            </div>


            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-background/80 hover:text-background transition-colors rounded-lg hover:bg-background/10"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden absolute top-full left-0 right-0 bg-foreground/97 backdrop-blur-xl border-b border-foreground overflow-hidden"
            >
              <div className="container px-4 py-4 flex flex-col gap-1">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.label}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    onClick={() => scrollToSection(item.href)}
                    className={`px-4 py-3.5 text-left rounded-xl transition-all duration-200 ${
                      activeSection === item.id
                        ? "text-background bg-background/15"
                        : "text-background/70 hover:text-background hover:bg-background/10"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      {activeSection === item.id && (
                        <motion.span
                          layoutId="mobileActive"
                          className="w-1.5 h-1.5 rounded-full bg-background"
                        />
                      )}
                      {item.label}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-40 p-3 bg-background backdrop-blur-lg border border-border rounded-full text-foreground hover:bg-muted transition-colors shadow-lg"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
